import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { format, parse } from "date-fns";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger, PopoverAnchor } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { REGION_ORDER, REGION_STYLE } from "@/lib/report";
import { Plus, Save, X, MapPin, CalendarIcon, Upload, Loader2, Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const fileUrl = (path) => `${API}/files/${path}`;
// Small server-side copy — used for every on-screen preview so pages don't pull multi-MB photos.
export const thumbUrl = (path) => `${API}/files/${path}?thumb=1`;

const parseDate = (str) => {
  if (!str) return undefined;
  for (const fmt of ["d MMMM yyyy", "d MMM yyyy"]) {
    const d = parse(str, fmt, new Date());
    if (!isNaN(d)) return d;
  }
  return undefined;
};

const filled = (v) => v !== "" && v !== null && v !== undefined;
const populatedCls = (v) => filled(v) && "border-amber-300 bg-amber-50/50 font-medium";

const FieldWrap = ({ label, className, children }) => (
  <div className={cn("space-y-1.5", className)}>
    <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</Label>
    {children}
  </div>
);

function ImageField({ value, onChange, testid }) {
  const [busy, setBusy] = useState(false);
  const items = (value || []).map((it) => (typeof it === "string" ? { path: it, scale: 1 } : it));

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const res = await axios.post(`${API}/upload`, fd);
        uploaded.push({ path: res.data.path, scale: 1 });
      }
      onChange([...items, ...uploaded]);
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const setScale = (path, scale) => onChange(items.map((it) => (it.path === path ? { path: it.path, scale } : it)));
  const remove = (path) => onChange(items.filter((it) => it.path !== path));

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-3 items-start">
        {items.map((it) => (
          <div key={it.path} className="w-24 space-y-1" data-testid={`${testid}-item`}>
            <div className="relative rounded-lg overflow-hidden border border-amber-200/80 group bg-amber-50/30"
              style={{ height: `${Math.round(44 * (it.scale || 1))}px` }}>
              <img src={thumbUrl(it.path)} alt="" className="w-full h-full object-cover" />
              <button type="button" onClick={() => remove(it.path)}
                className="absolute top-0 right-0 bg-black/60 text-white rounded-bl p-0.5 opacity-0 group-hover:opacity-100 transition">
                <X className="w-3 h-3" />
              </button>
            </div>
            <div className="flex items-center gap-1">
              <input type="range" min="0.5" max="3" step="0.1" value={it.scale || 1}
                onChange={(e) => setScale(it.path, Number(e.target.value))}
                data-testid={`${testid}-scale`} className="w-full accent-amber-500 cursor-pointer" />
              <span className="text-[10px] text-slate-400 w-8 text-right tabular-nums">{Math.round((it.scale || 1) * 100)}%</span>
            </div>
          </div>
        ))}
        <label className="w-16 h-16 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50/50 flex items-center justify-center cursor-pointer transition-all duration-200 hover:border-amber-400 hover:bg-amber-50/60 text-slate-400 hover:text-amber-600">
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
          <input type="file" accept="image/*" multiple className="hidden" onChange={handleFiles} data-testid={testid} />
        </label>
      </div>
    </div>
  );
}

export function MultiSelectField({ value, options, onChange, testid, placeholder, allowCustom }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const selected = Array.isArray(value) ? value : (value ? [value] : []);
  const toggle = (opt) => onChange(selected.includes(opt) ? selected.filter((v) => v !== opt) : [...selected, opt]);
  const remove = (opt) => onChange(selected.filter((v) => v !== opt));
  const trimmed = search.trim();
  const alreadyOffered = options.some((o) => o.toLowerCase() === trimmed.toLowerCase());
  const alreadySelected = selected.some((v) => v.toLowerCase() === trimmed.toLowerCase());
  const showAddCustom = allowCustom && trimmed && !alreadyOffered && !alreadySelected;
  const addCustom = () => { toggle(trimmed); setSearch(""); };

  return (
    <div className="space-y-1.5">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" data-testid={testid}
            className={cn(
              "w-full justify-between font-normal overflow-hidden rounded-lg transition-all duration-200",
              selected.length > 0 ? "border-amber-300 bg-amber-50/50 text-slate-800" : "text-slate-500",
              open && "border-amber-400 ring-2 ring-amber-100"
            )}>
            <span className="truncate">
              {selected.length ? `${selected.length} selected` : (placeholder || "Select…")}
            </span>
            <ChevronsUpDown className={cn("h-4 w-4 shrink-0 opacity-50 ml-2 transition-transform duration-200", open && "rotate-180")} />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-0 rounded-xl border-slate-200 shadow-lg overflow-hidden" align="start">
          <Command>
            <CommandInput placeholder={allowCustom ? "Type to filter or add…" : "Type to filter…"}
              value={search} onValueChange={setSearch} data-testid={`${testid}-search`} />
            <CommandList>
              <CommandEmpty>No match</CommandEmpty>
              <CommandGroup>
                {options.map((opt) => (
                  <CommandItem key={opt} value={opt} onSelect={() => toggle(opt)} data-testid={`${testid}-opt-${opt}`}>
                    <Check className={cn("mr-2 h-4 w-4 text-amber-600", selected.includes(opt) ? "opacity-100" : "opacity-0")} />
                    {opt}
                  </CommandItem>
                ))}
              </CommandGroup>
              {showAddCustom && (
                <CommandGroup heading="Custom" className="border-t border-dashed border-amber-200 mt-1">
                  <CommandItem value={trimmed} onSelect={addCustom} data-testid={`${testid}-add-custom`}
                    className="text-amber-700 data-[selected=true]:bg-amber-100 data-[selected=true]:text-amber-900">
                    <Plus className="mr-2 h-4 w-4 text-amber-500" />
                    Add "{trimmed}"
                  </CommandItem>
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {selected.map((opt) => (
            <span key={opt} data-testid={`${testid}-chip-${opt}`}
              className="inline-flex items-center gap-1 text-[11px] bg-amber-50 border border-amber-200 text-amber-800 rounded-full px-2 py-0.5 transition-colors hover:bg-amber-100">
              {opt}
              <button type="button" onClick={() => remove(opt)} className="text-amber-500 hover:text-red-600 transition-colors">
                <X className="w-2.5 h-2.5" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function ComboboxField({ value, options, onChange, testid, placeholder }) {
  const [open, setOpen] = useState(false);
  const [filtering, setFiltering] = useState(false);
  const inputRef = useRef(null);
  if (!options || options.length === 0) {
    return <Input data-testid={testid} value={value} placeholder={placeholder} className={cn(populatedCls(value))}
      onChange={(e) => onChange(e.target.value)} />;
  }
  const v = filtering ? (value || "").toLowerCase() : "";
  const filtered = v ? options.filter((o) => o.toLowerCase().includes(v)) : options;
  const selectOpt = (opt) => { onChange(opt); setFiltering(false); setOpen(false); inputRef.current?.blur(); };

  return (
    <Popover open={open} onOpenChange={(next) => { setOpen(next); if (!next) setFiltering(false); }}>
      <PopoverAnchor asChild>
        <Input ref={inputRef} data-testid={testid} value={value} placeholder={placeholder} autoComplete="off"
          className={cn(populatedCls(value))}
          onChange={(e) => { setFiltering(true); onChange(e.target.value); setOpen(true); }}
          onFocus={() => { setFiltering(false); setOpen(true); }} />
      </PopoverAnchor>
      <PopoverContent
        className="min-w-[220px] max-w-sm p-1 rounded-xl border-slate-200 shadow-lg max-h-56 overflow-y-auto"
        align="start" onOpenAutoFocus={(e) => e.preventDefault()} onCloseAutoFocus={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => { if (inputRef.current?.contains(e.target)) e.preventDefault(); }}
        onFocusOutside={(e) => { if (inputRef.current?.contains(e.target)) e.preventDefault(); }}
        onInteractOutside={(e) => { if (inputRef.current?.contains(e.target)) e.preventDefault(); }}>
        {filtered.length === 0 ? (
          <div className="px-3 py-2 text-sm text-slate-400">No preset match — free text kept</div>
        ) : filtered.map((opt) => (
          <div key={opt} data-testid={`${testid}-opt-${opt}`}
            onMouseDown={(e) => { e.preventDefault(); selectOpt(opt); }}
            className={cn("px-3 py-2 text-sm rounded-md cursor-pointer transition-colors hover:bg-amber-50 hover:text-amber-900",
              value === opt && "bg-amber-100 text-amber-900 font-semibold")}>
            {opt}
          </div>
        ))}
      </PopoverContent>
    </Popover>
  );
}


function formatContact(raw) {
  let d = String(raw ?? "").replace(/\D/g, ""); // strip +, spaces, dashes
  if (!d) return "";
  if (d.startsWith("0")) d = "60" + d.slice(1); // local 0XX -> 60
  d = d.slice(0, 13);
  let out = d.slice(0, 2);                       // 60
  if (d.length > 2) out += " " + d.slice(2, 4);  // NN
  if (d.length > 4) out += "-" + d.slice(4, 7);  // NNN
  if (d.length > 7) out += " " + d.slice(7, 11); // NNNN
  if (d.length > 11) out += " " + d.slice(11);   // extra
  return out;
}

function formatTime(raw) {
  const d = String(raw ?? "").replace(/\D/g, "").slice(0, 4);
  if (!d) return "";
  let hh = d.slice(0, 2);
  let mm = d.slice(2, 4);
  if (hh.length === 2 && Number(hh) > 23) hh = "23";
  if (mm.length === 2 && Number(mm) > 59) mm = "59";
  return d.length > 2 ? `${hh}:${mm}` : hh;
}

function spanClass(c) {
  if (c.type === "textarea" || c.type === "multiselect") return "col-span-2";
  if (c.key === "date" || c.key === "outlet") return "col-span-2"; // wider: full date / longer outlet
  return ""; // code and others stay narrow (single column)
}

function initFieldValue(c, editing) {
  const empty = (c.type === "image" || c.type === "multiselect") ? [] : "";
  if (!editing) return empty;
  return editing[c.key] ?? empty;
}

export default function DynamicForm({ report, editing, onSubmit, onCancel, horizontal, calendarMonth, onDateMonth }) {
  const [form, setForm] = useState({});
  const [region, setRegion] = useState(REGION_ORDER[0]);
  const [dateRed, setDateRed] = useState(false);
  const [rowHi, setRowHi] = useState(false);

  const cols = (report.columns || []).filter((c) => !c.computed);
  const meta = report.outlet_meta || {};
  const hasOutlet = cols.some((c) => c.key === "outlet");
  const codeToOutlet = {};
  Object.entries(meta).forEach(([name, info]) => { if (info.code) codeToOutlet[info.code] = name; });
  const vendors = report.vendors || [];
  const vendorMap = {};
  vendors.forEach((v) => (vendorMap[v.company] = v));
  const hasField = (k) => cols.some((c) => c.key === k);

  useEffect(() => {
    const init = {};
    cols.forEach((c) => (init[c.key] = initFieldValue(c, editing)));
    setForm(init);
    setRegion(editing?.region || REGION_ORDER[0]);
    setDateRed(!!editing?.date_red);
    setRowHi(!!editing?.row_highlight);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing, report.id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onOutlet = (v) => setForm((f) => {
    const next = { ...f, outlet: v };
    if (cols.some((c) => c.key === "code") && meta[v]) next.code = meta[v].code;
    return next;
  });

  const submit = (e) => {
    e.preventDefault();
    const data = {};
    cols.forEach((c) => {
      let v = form[c.key];
      if (c.type === "amount" || c.type === "number") v = v === "" ? "" : Number(v);
      if (c.type === "image") v = v || [];
      if (c.type === "multiselect") v = Array.isArray(v) ? v : (v ? [v] : []);
      data[c.key] = v ?? "";
    });
    const payload = { data, date_red: dateRed, row_highlight: rowHi };
    if (report.region_group && report.region_source === "field") payload.region = region;
    onSubmit(payload);
    if (!editing) {
      const reset = {};
      cols.forEach((c) => (reset[c.key] = (c.type === "image" || c.type === "multiselect") ? [] : ""));
      setForm(reset);
      setDateRed(false);
      setRowHi(false);
    }
  };

  const renderField = (c) => {
    const val = form[c.key] ?? "";
    if (c.type === "image") return <ImageField value={form[c.key] || []} onChange={(v) => set(c.key, v)} testid={`field-${c.key}`} />;
    if (c.type === "multiselect") {
      return <MultiSelectField value={form[c.key] || []} options={c.options || []}
        onChange={(v) => set(c.key, v)} testid={`field-${c.key}`} placeholder="Select or search…" allowCustom />;
    }
    if (c.type === "select") {
      const isOutlet = c.key === "outlet";
      return <ComboboxField value={val} options={c.options || []} testid={`field-${c.key}`} placeholder="Select or type"
        onChange={(v) => (isOutlet ? onOutlet(v) : set(c.key, v))} />;
    }
    if (c.type === "date") {
      return (
        <Popover>
          <PopoverTrigger asChild>
            <Button type="button" variant="outline" data-testid={`field-${c.key}`}
              className={cn("w-full justify-start font-normal overflow-hidden rounded-lg transition-all duration-200",
                !val && "text-muted-foreground",
                val && "border-amber-300 bg-amber-50/50 text-slate-900 ring-1 ring-amber-200", populatedCls(val))}>
              <CalendarIcon className="mr-2 h-4 w-4 shrink-0" /><span className="truncate">{val || "Pick a date"}</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 rounded-xl border-slate-200 shadow-xl" align="start">
            <Calendar mode="single" selected={parseDate(val)} defaultMonth={parseDate(val) || calendarMonth}
              onSelect={(d) => {
                if (!d) return;
                set(c.key, format(d, "d MMMM yyyy"));
                if (c.key === "date" && onDateMonth) onDateMonth(format(d, "MMM"));
              }} initialFocus />
          </PopoverContent>
        </Popover>
      );
    }
    if (c.type === "textarea") return <Textarea data-testid={`field-${c.key}`} rows={2} value={val}
      className={cn(populatedCls(val))} onChange={(e) => set(c.key, e.target.value)} />;
    if (c.type === "number" || c.type === "amount")
      return <Input data-testid={`field-${c.key}`} type="number" step={c.type === "amount" ? "0.01" : "1"} value={val}
        className={cn(populatedCls(val))} onChange={(e) => set(c.key, e.target.value)} placeholder="0" />;
    if (c.type === "time") {
      return <Input data-testid={`field-${c.key}`} value={val} inputMode="numeric" placeholder="HH:MM"
        className={cn(populatedCls(val))} onChange={(e) => set(c.key, formatTime(e.target.value))} />;
    }
    if (c.key === "contact_no" || c.key === "contact") {
      return <Input data-testid={`field-${c.key}`} value={val} inputMode="tel"
        className={cn("truncate", populatedCls(val))} placeholder="60 12-345 6789"
        onChange={(e) => set(c.key, formatContact(e.target.value))} />;
    }
    if (c.combobox || c.key === "vendor_company") {
      const comboOptions = (c.options && c.options.length)
        ? c.options
        : (c.key === "vendor_company" ? vendors.map((v) => v.company) : []);
      const onVendorChange = (v) => {
        setForm((f) => {
          const next = { ...f, [c.key]: v };
          const info = vendorMap[v];
          if (info) {
            if (hasField("pic")) next.pic = info.pic;
            if (hasField("contact_no")) next.contact_no = formatContact(info.contact);
            if (hasField("vendor") && !hasField("pic")) next.vendor = info.pic;
          }
          return next;
        });
      };
      return <ComboboxField value={val} options={comboOptions} testid={`field-${c.key}`} placeholder="Select or type"
        onChange={onVendorChange} />;
    }
    if (c.key === "code" && hasOutlet) {
      return <Input data-testid={`field-${c.key}`} value={val} placeholder="Type code e.g. EKO"
        className={cn(populatedCls(val))}
        onChange={(e) => {
          const v = e.target.value.toUpperCase();
          setForm((f) => {
            const next = { ...f, code: v };
            if (codeToOutlet[v]) next.outlet = codeToOutlet[v];
            return next;
          });
        }} />;
    }
    return <Input data-testid={`field-${c.key}`} value={val} className={cn(populatedCls(val))}
      onChange={(e) => set(c.key, c.key === "code" ? e.target.value.toUpperCase() : e.target.value)} />;
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="record-form">
      {report.region_group && report.region_source === "field" && (
        <FieldWrap label="Region">
          <ComboboxField value={region} options={REGION_ORDER} testid="field-region" placeholder="Select or type"
            onChange={setRegion} />
        </FieldWrap>
      )}
      <div className={cn("grid gap-3", horizontal ? "grid-cols-1 sm:grid-cols-2 md:grid-cols-4 xl:grid-cols-6" : "grid-cols-1 sm:grid-cols-2")}>
        {cols.map((c) => (
          <FieldWrap key={c.key} label={c.label} className={spanClass(c)}>
            {renderField(c)}
            {c.key === "outlet" && form.outlet && report.region_source === "outlet" && (
              <div className="flex items-center gap-1.5 text-xs font-semibold mt-1" style={{ color: REGION_STYLE[meta[form.outlet]?.region]?.text }}>
                <MapPin className="w-3 h-3" /> {meta[form.outlet]?.region}
              </div>
            )}
          </FieldWrap>
        ))}
      </div>
      {cols.some((c) => c.is_date) && (
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <Checkbox data-testid="check-date-red" checked={dateRed} onCheckedChange={(v) => setDateRed(!!v)} />
          <span className="text-sm text-slate-600">Highlight date in <span className="text-red-600 font-semibold">red</span></span>
        </label>
      )}
      {report.row_highlight && (
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <Checkbox data-testid="check-row-highlight" checked={rowHi} onCheckedChange={(v) => setRowHi(!!v)} />
          <span className="text-sm text-slate-600">Highlight entire row in <span className="font-semibold px-1.5 rounded" style={{ backgroundColor: "#FFF59D" }}>yellow</span> (e.g. closed outlet)</span>
        </label>
      )}
      <div className="flex gap-2 pt-1">
        <Button type="submit" data-testid="submit-record-btn" className={cn(horizontal ? "px-10" : "flex-1", "bg-slate-900 hover:bg-slate-800")}>
          {editing ? <><Save className="w-4 h-4 mr-1.5" />Update</> : <><Plus className="w-4 h-4 mr-1.5" />Add Record</>}
        </Button>
        {editing && <Button type="button" variant="outline" data-testid="cancel-edit-btn" onClick={onCancel}><X className="w-4 h-4" /></Button>}
      </div>
    </form>
  );
}
